import {
  type DocumentLoadEvent,
  type LoadError,
  Viewer,
  type ViewerProps,
} from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import {
  type HighlightArea,
  type RenderHighlightsProps,
  highlightPlugin,
} from "@react-pdf-viewer/highlight";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import React, { useEffect, useState, useCallback } from "react";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/page-navigation/lib/styles/index.css";
import "@react-pdf-viewer/highlight/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { GlobalWorkerOptions } from "pdfjs-dist";
import type {
  PDFDocumentProxy,
  TextItem,
} from "pdfjs-dist/types/src/display/api";

interface PdfViewerProps {
  highlights?: HighlightArea[];
  fileUrl: string;
}

const DevinPdfViewer: React.FC<PdfViewerProps> = ({
  highlights = [],
  fileUrl,
}) => {
  // console.log('DevinPdfViewer render start', { fileUrl, highlightsCount: highlights.length });

  const [isLoading, setIsLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [decodedFileUrl, setDecodedFileUrl] = useState<string>("");
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    // console.log('Setting GlobalWorkerOptions.workerSrc');
    GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";
    // console.log('Worker URL:', GlobalWorkerOptions.workerSrc);
  }, []);

  const pageNavigationPluginInstance = pageNavigationPlugin();
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const highlightPluginInstance = highlightPlugin({
    renderHighlights: (props: RenderHighlightsProps) => {
      return (
        <div>
          {highlights
            .filter((highlight) => highlight.pageIndex === props.pageIndex)
            .map((highlight, index) => {
              const top = highlight.top * 100;
              const left = highlight.left * 100;
              const width = highlight.width * 100;
              const height = highlight.height * 100;

              return (
                <div
                  key={index}
                  style={Object.assign(
                    {},
                    {
                      background: "yellow",
                      opacity: 0.3,
                    },
                    props.getCssProperties(
                      {
                        pageIndex: highlight.pageIndex,
                        top,
                        left,
                        width,
                        height,
                      },
                      props.rotation,
                    ),
                  )}
                />
              );
            })}
        </div>
      );
    },
  });

  const { jumpToHighlightArea } = highlightPluginInstance;

  useEffect(() => {
    if (jumpToHighlightArea && highlights.length > 0) {
      jumpToHighlightArea(highlights[0]);
    }
  }, [jumpToHighlightArea, highlights]);

  const handleDocumentLoad = useCallback((event: DocumentLoadEvent) => {
    // console.log('handleDocumentLoad called');
    try {
      const doc = event.doc as unknown as PDFDocumentProxy;
      // console.log('PDF document loaded successfully', { numPages: doc.numPages });
      setNumPages(doc.numPages);
      setPdfDocument(doc);
      setIsLoading(false);

      doc.getMetadata().then((metadata) => {
        // console.log('PDF metadata:', metadata);
      });

      if ("getDownloadInfo" in doc) {
        (doc as any).getDownloadInfo().then((info: any) => {
          // console.log('Download info:', info)
        });
      }

      // console.log('PDF fingerprints:', doc.fingerprints);
      if ("version" in doc) {
        console.log("PDF version:", (doc as any).version);
      }

      doc.getPage(1).then((page) => {
        const viewport = page.getViewport({ scale: 1.0 });
        // console.log('First page size:', viewport.width, 'x', viewport.height);
      });
    } catch (error) {
      console.error("Error processing PDF document:", error);
      setPdfError(`Error processing PDF: ${error.message}`);
    }
  }, []);

  const renderError = useCallback((error: LoadError) => {
    console.error("Error rendering PDF:", error);
    console.error("Error details:", JSON.stringify(error, null, 2));
    setPdfError(`${error.name}: ${error.message}`);
    return (
      <div>
        <p>An error occurred while loading the PDF:</p>
        <p>{error.message || error.name}</p>
        <p>Please check the console for more details.</p>
      </div>
    );
  }, []);

  const searchAndHighlight = useCallback(
    async (searchTerm: string): Promise<HighlightArea[]> => {
      console.log("searchAndHighlight called", { searchTerm });
      if (!pdfDocument) {
        console.error("PDF document not loaded");
        return [];
      }

      const highlights: HighlightArea[] = [];
      const searchRegex = new RegExp(`\\b${searchTerm}\\b`, "gi");

      for (let pageIndex = 0; pageIndex < pdfDocument.numPages; pageIndex++) {
        const page = await pdfDocument.getPage(pageIndex + 1);
        const textContent = await page.getTextContent();
        const viewport = page.getViewport({ scale: 1.0 });

        for (const item of textContent.items) {
          const textItem = item as TextItem;
          const matches = [...textItem.str.matchAll(searchRegex)];

          for (const match of matches) {
            const startIndex = match.index!;
            const endIndex = startIndex + match[0].length;

            const startX =
              textItem.transform[4] +
              (startIndex / textItem.str.length) * textItem.width;
            const width =
              ((endIndex - startIndex) / textItem.str.length) * textItem.width;

            highlights.push({
              pageIndex,
              height: (textItem.height / viewport.height) * 100,
              left: (textItem.transform[4] / viewport.width) * 100,
              top:
                ((viewport.height - (textItem.transform[5] + textItem.height)) /
                  viewport.height) *
                100,
              width: (width / viewport.width) * 100,
            });
          }
        }
      }

      console.log(`Found ${highlights.length} highlights for "${searchTerm}"`);
      return highlights;
    },
    [pdfDocument],
  );

  useEffect(() => {
    // console.log('DevinPdfViewer useEffect', { fileUrl });
    const decodeUrl = (url: string) => {
      // console.log('Decoding URL:', url);
      // The URL should already be correct, so we don't need to modify it
      // console.log('Decoded URL:', url);
      return url;
    };

    setDecodedFileUrl(decodeUrl(fileUrl));
  }, [fileUrl]);

  const handleError = useCallback((error: Error) => {
    console.error("Error loading PDF:", error);
    setPdfError(error.message);
  }, []);

  const viewerProps: ViewerProps = {
    fileUrl: decodedFileUrl,
    plugins: [
      pageNavigationPluginInstance,
      highlightPluginInstance,
      defaultLayoutPluginInstance,
    ],
    renderError: renderError,
    onDocumentLoad: handleDocumentLoad,
    renderLoader: (percentages: number) => {
      // console.log(`PDF loading progress: ${Math.round(percentages)}%`);
      return (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <div>Loading PDF... {Math.round(percentages)}%</div>
        </div>
      );
    },
    defaultScale: scale,
    onError: handleError,
  };

  // console.log('DevinPdfViewer render end', { decodedFileUrl, pdfError });

  return (
    <ErrorBoundary>
      <div className="bg-secondary h-[calc(100%-1rem)]">
        {decodedFileUrl ? (
          <>
            {pdfError ? (
              <div>Error loading PDF: {pdfError}</div>
            ) : (
              <Viewer
                {...viewerProps}
                fileUrl={decodedFileUrl}
                key={decodedFileUrl}
              />
            )}
          </>
        ) : (
          <div>Loading PDF URL...</div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default DevinPdfViewer;
// ErrorBoundary component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong.</h1>;
    }

    return this.props.children;
  }
}
